import { formatInTimeZone } from "date-fns-tz";
import { PLANNING_TZ } from "@/lib/timezone";
import type { Order, Customer, Tractor, Driver, Location } from "@/types/tms";

export type ExportableOrder = Order & {
  billing_party?: Customer | null;
  assigned_tractor?: Tractor | null;
  assigned_operator?: Driver | null;
  assigned_operator_2?: Driver | null;
};

export const ORDERS_CSV_HEADERS = [
  "Order ID",
  "Status",
  "Trip Type",
  "Ship From",
  "Ready Time",
  "Ship To",
  "Delivery Time",
  "Tractor",
  "Trailer ID",
  "Trailer Type",
  "Load Status",
  "Tender ID",
  "Billing Party",
  "Operator",
] as const;

function escapeCsv(value: unknown): string {
  if (value === null || value === undefined) return "";
  let s = String(value);
  if (s === "") return "";
  // Neutralize spreadsheet formula injection (=, +, -, @, tab, CR).
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function joinAddress(parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? "").trim())
    .filter((p) => p.length > 0)
    .join(", ");
}

function formatTimeAt(
  iso: string | null | undefined,
  tz: string,
): string {
  if (!iso) return "";
  try {
    return formatInTimeZone(new Date(iso), tz, "yyyy-MM-dd HH:mm zzz");
  } catch {
    return "";
  }
}

function findLocationFor(
  order: ExportableOrder,
  side: "from" | "to",
  locations: Location[] | undefined,
): Location | undefined {
  if (!locations?.length) return undefined;
  const addr = side === "from" ? order.ship_from_street_address : order.ship_to_street_address;
  const city = side === "from" ? order.ship_from_city : order.ship_to_city;
  if (!addr && !city) return undefined;
  return locations.find(
    (l) =>
      (l.street_address ?? "") === (addr ?? "") &&
      (l.city ?? "") === (city ?? ""),
  );
}

function operatorName(order: ExportableOrder): string {
  const a = order.assigned_operator?.name?.trim() || "";
  const b = order.assigned_operator_2?.name?.trim() || "";
  if (a && b) return `${a} / ${b}`;
  return a || b;
}

function loadStatusLabel(order: ExportableOrder): string {
  const v = (order as { trailer_load_status?: string | null }).trailer_load_status;
  if (!v) return "";
  const norm = v.toLowerCase();
  if (norm === "loaded") return "Loaded";
  if (norm === "empty") return "Empty";
  return "";
}

function trailerTypeName(
  order: ExportableOrder,
  trailerTypes: Array<{ id: string; label: string }> | undefined,
): string {
  if (!order.trailer_type_id) return "";
  return trailerTypes?.find((t) => t.id === order.trailer_type_id)?.label ?? "";
}

export function ordersToCsv(
  orders: ExportableOrder[],
  opts: {
    locations?: Location[];
    trailerTypes?: Array<{ id: string; label: string }>;
    trailerLoadStatusByTrailerId?: Map<string, string | null>;
    /** At-delivery load-status snapshots keyed by order id (delivered orders). */
    loadStatusAtDelivery?: Map<string, { load_status: string | null }>;
  } = {},
): string {
  const { locations, trailerTypes, trailerLoadStatusByTrailerId, loadStatusAtDelivery } = opts;
  const lines: string[] = [];
  lines.push(ORDERS_CSV_HEADERS.map(escapeCsv).join(","));

  for (const o of orders) {
    const fromLoc = findLocationFor(o, "from", locations);
    const toLoc = findLocationFor(o, "to", locations);
    // Exported files leave the app, so like emails they use Central
    // (docs/TIMEZONES.md), not the stop's or the viewer's timezone.
    const fromTz = PLANNING_TZ;
    const toTz = PLANNING_TZ;
    // Delivered orders export the trailer's load status *at delivery*; the
    // trailer's live value has usually moved on to a later trip.
    const historical =
      o.status === "delivered" ? loadStatusAtDelivery?.get((o as { id?: string }).id ?? "") : undefined;
    const resolvedLoad =
      historical?.load_status ??
      (trailerLoadStatusByTrailerId && o.trailer_id
        ? trailerLoadStatusByTrailerId.get(o.trailer_id) ?? null
        : undefined);
    const enriched: ExportableOrder =
      resolvedLoad !== undefined
        ? (Object.assign({}, o, { trailer_load_status: resolvedLoad }) as ExportableOrder)
        : o;

    const row = [
      o.unique_id ?? "",
      o.status ?? "",
      o.trip_type ?? "",
      joinAddress([
        o.ship_from_street_address,
        o.ship_from_city,
        o.ship_from_province_state,
      ]),
      formatTimeAt(o.ready_time, fromTz),
      joinAddress([
        o.ship_to_street_address,
        o.ship_to_city,
        o.ship_to_province_state,
      ]),
      formatTimeAt((o as { requested_delivery_time?: string | null }).requested_delivery_time, toTz),
      o.assigned_tractor?.name ?? "",
      o.trailer_id ?? "",
      trailerTypeName(o, trailerTypes),
      loadStatusLabel(enriched),
      o.tender_id ?? "",
      o.billing_party?.organization_name ?? "",
      operatorName(o),
    ];

    lines.push(row.map(escapeCsv).join(","));
  }

  return lines.join("\r\n");
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function buildExportFilename(now: Date = new Date()): string {
  const stamp = formatInTimeZone(now, PLANNING_TZ, "yyyy-MM-dd-HHmm");
  return `orders-export-${stamp}.csv`;
}